import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";

export type Tone = "neutral" | "good" | "warn" | "bad" | "info";

const numberFormat = new Intl.NumberFormat("en-GB");
export const formatNumber = (n: number | null | undefined): string =>
  n == null || Number.isNaN(n) ? "–" : numberFormat.format(n);

export const formatRelative = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const abs = Math.abs(diffMs);
  const minutes = Math.round(abs / 60000);
  const hours = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const text =
    minutes < 1 ? "just now" : minutes < 60 ? `${minutes}m` : hours < 48 ? `${hours}h` : `${days}d`;
  if (text === "just now") return text;
  return diffMs >= 0 ? `${text} ago` : `in ${text}`;
};

export const formatShortDate = (value: string | Date | null | undefined): string => {
  if (!value) return "–";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "–";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/** Tone for an issue count: zero is good, anything else uses `whenPositive`. */
export const countTone = (n: number | null | undefined, whenPositive: Tone = "warn"): Tone =>
  n == null ? "neutral" : n === 0 ? "good" : whenPositive;

interface KpiProps {
  label: string;
  value: number | string | null | undefined;
  tone?: Tone;
  hint?: React.ReactNode;
  to?: string;
  emphasis?: boolean;
}

export const Kpi: React.FC<KpiProps> = ({ label, value, tone = "neutral", hint, to, emphasis }) => {
  const content = (
    <>
      <span className="home-kpi__value">
        {typeof value === "number" ? formatNumber(value) : value ?? "–"}
      </span>
      <span className="home-kpi__label">{label}</span>
      {hint && <span className="home-kpi__hint">{hint}</span>}
    </>
  );
  const className = `home-kpi home-kpi--${tone}${emphasis ? " home-kpi--emphasis" : ""}${to ? " home-kpi--link" : ""}`;
  return to ? (
    <Link to={to} className={className}>
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
};

export const KpiGrid: React.FC<{ children: React.ReactNode; columns?: 2 | 3 | 4 }> = ({
  children,
  columns = 3,
}) => <div className={`home-kpi-grid home-kpi-grid--${columns}`}>{children}</div>;

export interface BarItem {
  label: string;
  count: number;
  to?: string;
}

/** Ranked horizontal bars, e.g. "top issue types". */
export const IssueBars: React.FC<{ title?: string; items: BarItem[]; emptyText?: string }> = ({
  title,
  items,
  emptyText = "Nothing to show",
}) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="home-bars">
      {title && <div className="home-section-title">{title}</div>}
      {items.length === 0 ? (
        <div className="home-muted">{emptyText}</div>
      ) : (
        items.map((item) => {
          const row = (
            <>
              <span className="home-bars__label">{item.label}</span>
              <span className="home-bars__track">
                <span className="home-bars__fill" style={{ width: `${(item.count / max) * 100}%` }} />
              </span>
              <span className="home-bars__count">{formatNumber(item.count)}</span>
            </>
          );
          return item.to ? (
            <Link key={item.label} to={item.to} className="home-bars__row home-bars__row--link">
              {row}
            </Link>
          ) : (
            <div key={item.label} className="home-bars__row">
              {row}
            </div>
          );
        })
      )}
    </div>
  );
};

export const ListRows: React.FC<{
  title?: string;
  rows: Array<{ key: string; primary: React.ReactNode; secondary?: React.ReactNode; to?: string }>;
  emptyText?: string;
}> = ({ title, rows, emptyText = "Nothing coming up" }) => (
  <div className="home-list">
    {title && <div className="home-section-title">{title}</div>}
    {rows.length === 0 ? (
      <div className="home-muted">{emptyText}</div>
    ) : (
      rows.map((r) => {
        const inner = (
          <>
            <span className="home-list__primary">{r.primary}</span>
            {r.secondary && <span className="home-list__secondary">{r.secondary}</span>}
          </>
        );
        return r.to ? (
          <Link key={r.key} to={r.to} className="home-list__row home-list__row--link">
            {inner}
          </Link>
        ) : (
          <div key={r.key} className="home-list__row">
            {inner}
          </div>
        );
      })
    )}
  </div>
);

export const CardLoading: React.FC<{ rows?: number }> = ({ rows = 2 }) => (
  <div className="home-card-loading">
    <div className="home-kpi-grid home-kpi-grid--3">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} height="4.25rem" borderRadius="10px" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} height="1rem" className="mt-3" width={`${90 - i * 15}%`} />
    ))}
  </div>
);

export const CardError: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div className="home-card-state home-card-state--error">
    <i className="pi pi-exclamation-triangle" />
    <span>{message}</span>
    {onRetry && <Button label="Retry" size="small" text onClick={onRetry} />}
  </div>
);

export const CardEmpty: React.FC<{
  icon?: string;
  title: string;
  body?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon = "pi pi-check-circle", title, body, action }) => (
  <div className="home-card-state">
    <i className={icon} />
    <strong>{title}</strong>
    {body && <span>{body}</span>}
    {action}
  </div>
);

export const Freshness: React.FC<{ label?: string; at: string | Date | null | undefined }> = ({
  label = "Updated",
  at,
}) => {
  // Date-only values (e.g. "2026-10-26") read better as a date than "13h ago".
  const rel =
    typeof at === "string" && /^\d{4}-\d{2}-\d{2}$/.test(at)
      ? `as of ${formatShortDate(at)}`
      : formatRelative(at);
  return rel ? (
    <span className="home-freshness">
      <i className="pi pi-clock" /> {label} {rel}
    </span>
  ) : null;
};
